import _ from 'lodash';
import { canadianProvincesMap, JamaicanParishesMap, saintVincentAndTheGrenadinesParishesMap } from '../constants/geography';
import { stateComplianceRules, canadaDefaults, jamaicanDefaults, saintVincentAndTheGrenadinesDefaults } from '../constants/compliance';
var OVERRIDE_KEYS = [
    'stateComplianceRules',
    'canadaDefaults',
    'jamaicanDefaults',
    'saintVincentAndTheGrenadinesDefaults'
];
// The statically-bundled config is always retained as the legal safety fallback.
var staticConfig = {
    stateComplianceRules: stateComplianceRules,
    canadaDefaults: canadaDefaults,
    jamaicanDefaults: jamaicanDefaults,
    saintVincentAndTheGrenadinesDefaults: saintVincentAndTheGrenadinesDefaults
};
// Populated by the remote-config loaders (API service / browser bootstrap) when the
// remote compliance config is enabled and successfully loaded. Null means "use static".
// IMPORTANT: this is process-global state — set once at load/poll time, never per-request —
// so a single value is shared safely across all concurrent requests in the API process.
var overrideConfig = null;
/**
 * Applies a remote compliance config override. Each of the four maps independently falls
 * back to the statically-bundled config when the override omits it.
 *
 * @param {{
 *   stateComplianceRules?: object,
 *   canadaDefaults?: object,
 *   jamaicanDefaults?: object,
 *   saintVincentAndTheGrenadinesDefaults?: object,
 * }} config
 * @returns {boolean} true if applied; false if the shape was invalid (previous config kept)
 */ export var setComplianceConfigOverride = function(config) {
    // Guard against malformed remote payloads (e.g. bad JSON/module from S3). Without this a
    // typo'd shape would look "set" but silently fall back to static for every map.
    var isValid = _.isPlainObject(config) && _.some(OVERRIDE_KEYS, function(key) {
        return _.isPlainObject(config[key]);
    });
    if (!isValid) {
        return false;
    }
    overrideConfig = config;
    return true;
};
export var clearComplianceConfigOverride = function() {
    overrideConfig = null;
};
// Resolves the four config maps, preferring the override per-key and falling back to
// the static value for any map the override omits.
var getActiveConfig = function() {
    if (!overrideConfig) {
        return staticConfig;
    }
    var _overrideConfig_stateComplianceRules, _overrideConfig_canadaDefaults, _overrideConfig_jamaicanDefaults, _overrideConfig_saintVincentAndTheGrenadinesDefaults;
    return {
        stateComplianceRules: (_overrideConfig_stateComplianceRules = overrideConfig.stateComplianceRules) !== null && _overrideConfig_stateComplianceRules !== void 0 ? _overrideConfig_stateComplianceRules : staticConfig.stateComplianceRules,
        canadaDefaults: (_overrideConfig_canadaDefaults = overrideConfig.canadaDefaults) !== null && _overrideConfig_canadaDefaults !== void 0 ? _overrideConfig_canadaDefaults : staticConfig.canadaDefaults,
        jamaicanDefaults: (_overrideConfig_jamaicanDefaults = overrideConfig.jamaicanDefaults) !== null && _overrideConfig_jamaicanDefaults !== void 0 ? _overrideConfig_jamaicanDefaults : staticConfig.jamaicanDefaults,
        saintVincentAndTheGrenadinesDefaults: (_overrideConfig_saintVincentAndTheGrenadinesDefaults = overrideConfig.saintVincentAndTheGrenadinesDefaults) !== null && _overrideConfig_saintVincentAndTheGrenadinesDefaults !== void 0 ? _overrideConfig_saintVincentAndTheGrenadinesDefaults : staticConfig.saintVincentAndTheGrenadinesDefaults
    };
};
export var hasComplianceConfigForState = function(state) {
    var _getActiveConfig = getActiveConfig(), activeStateComplianceRules = _getActiveConfig.stateComplianceRules;
    return _.has(activeStateComplianceRules, state) || Boolean(canadianProvincesMap[state]) || _.includes(_.values(JamaicanParishesMap), state) || _.includes(_.keys(saintVincentAndTheGrenadinesParishesMap), state);
};
export var getComplianceConfig = function(state, key) {
    var _getActiveConfig = getActiveConfig(), activeStateComplianceRules = _getActiveConfig.stateComplianceRules, activeCanadaDefaults = _getActiveConfig.canadaDefaults, activeJamaicanDefaults = _getActiveConfig.jamaicanDefaults, activeSaintVincentAndTheGrenadinesDefaults = _getActiveConfig.saintVincentAndTheGrenadinesDefaults;
    // all of canada has the same compliance info except for Alberta and Ontario
    var stateHasConfig = _.includes(_.keys(activeStateComplianceRules), state);
    if (!stateHasConfig && canadianProvincesMap[state]) {
        return _.get(activeCanadaDefaults, key, _.get(activeStateComplianceRules.DEFAULT, key));
    }
    // Jamaica
    // we only have one jamaican dispo currently!
    if (!stateHasConfig && _.includes(_.values(JamaicanParishesMap), state)) {
        return _.get(activeJamaicanDefaults, key, _.get(activeStateComplianceRules.DEFAULT, key));
    }
    // Saint Vincent and the Grenadines
    if (!stateHasConfig && _.includes(_.keys(saintVincentAndTheGrenadinesParishesMap), state)) {
        return _.get(activeSaintVincentAndTheGrenadinesDefaults, key, _.get(activeStateComplianceRules.DEFAULT, key));
    }
    return _.get(activeStateComplianceRules, "".concat(state, ".").concat(key), _.get(activeStateComplianceRules.DEFAULT, key));
};
